import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type NotificationType = 'success' | 'error' | 'warning' | 'info'

export interface Notification {
  id: string
  type: NotificationType
  title: string
  message?: string
  timestamp: number
  read: boolean
}

interface NotificationState {
  notifications: Notification[]
  unreadCount: number
}

interface NotificationActions {
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void
  markAsRead: (id: string) => void
  markAllAsRead: () => void
  dismissNotification: (id: string) => void
  clearAllNotifications: () => void
}

type NotificationStore = NotificationState & NotificationActions

const MAX_NOTIFICATIONS = 100

export const useNotificationStore = create<NotificationStore>()(
  persist(
    (set) => ({
      notifications: [],
      unreadCount: 0,

      addNotification: (notification) => set((state) => {
        const newNotification: Notification = {
          ...notification,
          id: crypto.randomUUID(),
          timestamp: Date.now(),
          read: false,
        }
        const updatedNotifications = [newNotification, ...state.notifications]
        // Enforce 100 item limit (remove oldest)
        if (updatedNotifications.length > MAX_NOTIFICATIONS) {
          updatedNotifications.length = MAX_NOTIFICATIONS
        }
        return {
          notifications: updatedNotifications,
          unreadCount: state.unreadCount + 1,
        }
      }),

      markAsRead: (id) => set((state) => {
        const notification = state.notifications.find(n => n.id === id)
        if (notification && !notification.read) {
          return {
            notifications: state.notifications.map(n =>
              n.id === id ? { ...n, read: true } : n
            ),
            unreadCount: Math.max(0, state.unreadCount - 1),
          }
        }
        return state
      }),

      markAllAsRead: () => set((state) => ({
        notifications: state.notifications.map(n => ({ ...n, read: true })),
        unreadCount: 0,
      })),

      dismissNotification: (id) => set((state) => {
        const notification = state.notifications.find(n => n.id === id)
        const wasUnread = notification && !notification.read
        return {
          notifications: state.notifications.filter(n => n.id !== id),
          unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
        }
      }),

      clearAllNotifications: () => set({
        notifications: [],
        unreadCount: 0,
      }),
    }),
    {
      name: 'mazpin-notifications',
    }
  )
)
